import { storeListFromResponse } from "../../service/storeResponse";
import { hasStoreAddress, isCoordinateString, savedStoreDetails, matchesSavedStore, normalizeStoreStatus, isStoreActive } from "./storeAddress";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiEye } from "react-icons/fi";
import { Filter, Pencil, Plus, RotateCcw, Search, Store, Trash2, X } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import BackButton from "../common/BackButton";
import CommonButton from "../common/CommonButton";
import ActionIconButton from "../common/ActionIconButton";
import TableScrollShell from "../common/TableScrollShell";
import PageSizeSelector from "../common/PageSizeSelector";
import PagerArrowButton from "../common/PagerArrowButton";
import SearchFieldPicker from "../common/SearchFieldPicker";
import DeletePermission from "../suspence/DeletePermission";
import useDirection from "../../hooks/useDirection";
import CreateStoreModal, { formatAddressLocation, reverseGeocode } from "./CreateStoreModal";
import {
  createStoreApi,
  deleteStoreApi,
  getStoreListApi,
  getStoresByProjectIdApi,
  getStoreByIdApi,
  updateStoreApi,
} from "../../service/Inventory";
import { toast } from "../../utils/toast";

const STORE_CACHE_KEY = "blufield_store_details_cache";
const COLUMN_FILTER_POPOVER_WIDTH = 232;

const getStoreCache = () => {
  try {
    const raw = localStorage.getItem(STORE_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const saveStoreCache = (id, data) => {
  if (!id) return;
  try {
    const current = getStoreCache();
    current[String(id)] = {
      ...(current[String(id)] || {}),
      ...data,
    };
    localStorage.setItem(STORE_CACHE_KEY, JSON.stringify(current));
  } catch (e) {}
};

const removeStoreCache = (id) => {
  if (!id) return;
  try {
    const current = getStoreCache();
    delete current[String(id)];
    localStorage.setItem(STORE_CACHE_KEY, JSON.stringify(current));
  } catch (e) {}
};

const ColumnFilterPopover = ({
  options,
  selected,
  onChange,
  onClose,
  anchorEl,
  theme,
  isRtl,
}) => {
  const [q, setQ] = useState("");
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);
  const dark = theme === "dark";
  const highContrast = theme === "high-contrast";

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, isRtl ? rect.left : rect.right - COLUMN_FILTER_POPOVER_WIDTH),
        window.innerWidth - COLUMN_FILTER_POPOVER_WIDTH - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl, isRtl]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, anchorEl]);

  const list = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const allValues = options.map((o) => o.value);
  const isChecked = (value) => (selected === null ? true : selected.has(value));
  const toggle = (v) => {
    const base = selected === null ? new Set(allValues) : new Set(selected);
    base.has(v) ? base.delete(v) : base.add(v);
    onChange(base.size === allValues.length ? null : base);
  };

  if (!popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: "fixed",
        top: popoverStyle.top,
        left: popoverStyle.left,
        width: COLUMN_FILTER_POPOVER_WIDTH,
      }}
      className={`z-[1500] rounded-lg border shadow-xl p-3 normal-case font-normal text-xs ${
        highContrast
          ? "border-white bg-black text-white"
          : dark
            ? "border-slate-700 bg-slate-800 text-slate-200"
            : "border-slate-200 bg-white text-slate-700"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative mb-2">
        <Search size={13} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 ${isRtl ? "right-2" : "left-2"}`} />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className={`w-full py-1.5 text-xs rounded-md border outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${
            isRtl ? "pr-7 pl-2" : "pl-7 pr-2"
          } ${
            highContrast
              ? "border-white bg-black text-white"
              : dark
                ? "border-slate-600 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-800"
          }`}
        />
      </div>
      <div className="max-h-44 overflow-y-auto space-y-0.5 custom-scrollbar">
        {list.length === 0 && <div className="text-xs px-1.5 py-2 text-slate-400">No matches</div>}
        {list.map((o) => (
          <label
            key={o.value}
            className={`flex items-center gap-2 px-1.5 py-1 text-xs rounded cursor-pointer transition-colors ${
              dark ? "hover:bg-slate-700 text-slate-200" : "hover:bg-slate-50 text-slate-700"
            }`}
          >
            <input
              type="checkbox"
              checked={isChecked(o.value)}
              onChange={() => toggle(o.value)}
              className="accent-[#236DB4] w-3.5 h-3.5 shrink-0 rounded"
            />
            <span className="min-w-0 flex-1 truncate">{o.label}</span>
          </label>
        ))}
      </div>
      <div className={`flex items-center justify-between mt-2 pt-2 border-t ${
        dark ? "border-slate-700" : highContrast ? "border-white" : "border-slate-200"
      }`}>
        <div className="flex items-center gap-2 text-xs font-medium">
          <button type="button" onClick={() => onChange(null)} className="text-[#236DB4] hover:underline">All</button>
          <button type="button" onClick={() => onChange(new Set())} className="text-slate-400 hover:underline">None</button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="h-7 px-3 rounded-md text-xs font-semibold text-white bg-[#236DB4] hover:bg-[#1d5a96] transition-colors"
        >
          OK
        </button>
      </div>
    </div>,
    document.body
  );
};

const CreateStorePage = ({ stores = [], onEditStore }) => {
  const { theme, langMode, projectId: contextProjectId, selectedOrgId } = useContext(AppContext);
  const { isRTL } = useDirection();
  const dark = theme === "dark";
  const highContrast = theme === "high-contrast";
  const t = translations[langMode] || {};
  const activeProjectId = Number(contextProjectId || localStorage.getItem("projectId") || 2);
  const activeOrgId = Number(
    selectedOrgId ||
    localStorage.getItem("selectedOrgId") ||
    (() => {
      try {
        const u = JSON.parse(localStorage.getItem("userDetails"));
        return u?.organization?.organizationId || u?.organizationId;
      } catch {
        return null;
      }
    })() ||
    2
  );

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchScope, setSearchScope] = useState("storeName");
  const [columnFilters, setColumnFilters] = useState({
    storeName: null,
    location: null,
    status: null,
  });
  const [openFilterPopoverColId, setOpenFilterPopoverColId] = useState(null);
  const filterButtonRefs = useRef({});
  const [storeToDelete, setStoreToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [localStores, setLocalStores] = useState(() => {
    try {
      const saved = localStorage.getItem("blufield_stores");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed.map((s) => ({ ...s, status: normalizeStoreStatus(s.status) }));
        }
      }
    } catch (e) {}
    return stores && stores.length > 0 ? stores.map((s) => ({ ...s, status: normalizeStoreStatus(s.status) })) : [];
  });

  const filterOptionsByCol = useMemo(() => {
    const storeNameSet = new Set();
    const locationSet = new Set();

    localStores.forEach((s) => {
      const name = s.name || s.storeName;
      if (name) storeNameSet.add(name);
      if (s.location && s.location !== "—") locationSet.add(s.location);
    });

    return {
      storeName: Array.from(storeNameSet).sort().map((name) => ({ value: name, label: name })),
      location: Array.from(locationSet).sort().map((loc) => ({ value: loc, label: loc })),
      status: [
        { value: "active", label: t.storeActive || "Active" },
        { value: "inactive", label: t.storeInactive || "Inactive" },
      ],
    };
  }, [localStores, t.storeActive, t.storeInactive]);

  const fetchStores = useCallback(async (submittedStore = null) => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      let list = [];
      try {
        const res = await getStoreListApi({
          projectId: activeProjectId,
          page: 0,
          size: 100,
        });
        list = storeListFromResponse(res);
      } catch (listErr) {
        console.warn("getStoreListApi failed, falling back to getStoresByProjectIdApi:", listErr);
        const fallbackRes = await getStoresByProjectIdApi(activeProjectId);
        list = storeListFromResponse(fallbackRes);
      }

      const cache = getStoreCache();

      const resolvedStores = list.map((item, idx) => {
        const storeId = item.id ?? item.storeId ?? `store-${idx}`;
        const saved = cache[String(storeId)] || {};
        const cached = savedStoreDetails(item, saved, submittedStore);

        const lat = item.latitude != null ? Number(item.latitude) : cached.latitude != null ? Number(cached.latitude) : null;
        const lng = item.longitude != null ? Number(item.longitude) : cached.longitude != null ? Number(cached.longitude) : null;
        let location = cached.location || item.location || "";
        const address = cached.address || item.address || null;

        if (hasStoreAddress(address)) location = formatAddressLocation(address);
        if (isCoordinateString(location)) location = "";
        if ((!location || location === "—") && Number.isFinite(lat) && Number.isFinite(lng)) {
          location = formatAddressLocation(address, lat, lng);
          reverseGeocode(lat, lng, langMode)
            .then((geoAddr) => {
              if (hasStoreAddress(geoAddr)) {
                const readable = formatAddressLocation(geoAddr, lat, lng);
                if (readable && readable !== "—") {
                  const latest = getStoreCache()[String(storeId)];
                  if (Number(latest?.latitude) !== lat || Number(latest?.longitude) !== lng || hasStoreAddress(latest?.address)) return;
                  saveStoreCache(storeId, { location: readable, address: geoAddr });
                  setLocalStores((prev) =>
                    prev.map((s) => (s.id === storeId ? { ...s, location: readable, address: geoAddr } : s))
                  );
                }
              }
            })
            .catch(() => {});
        }

        const rawStatus = item.storeStatus ?? item.status ?? item.activeStatus ?? item.isActive ?? cached.storeStatus ?? cached.status;
        const submittedMatch = submittedStore && matchesSavedStore(item, submittedStore);
        const resolvedStatus = submittedMatch && (submittedStore.storeStatus != null || submittedStore.status != null)
          ? (submittedStore.storeStatus || submittedStore.status)
          : rawStatus;
        const normalizedStatus = normalizeStoreStatus(resolvedStatus);

        saveStoreCache(storeId, {
          latitude: lat,
          longitude: lng,
          location,
          address,
          status: normalizedStatus,
          storeStatus: normalizedStatus === "inactive" ? "INACTIVE" : "ACTIVE",
        });

        return {
          id: storeId,
          name: item.storeName ?? item.name ?? cached.storeName ?? "",
          storeName: item.storeName ?? item.name ?? cached.storeName ?? "",
          latitude: lat,
          longitude: lng,
          location: location || "—",
          status: normalizedStatus,
          storeStatus: normalizedStatus === "inactive" ? "INACTIVE" : "ACTIVE",
          address,
          projectId: item.projectId ?? activeProjectId,
          raw: item,
        };
      });

      // Preserve known inactive stores so they remain visible in the table with Inactive status
      // (the backend store/list query by default only returns active stores)
      const currentSaved = (() => {
        try {
          const raw = localStorage.getItem("blufield_stores");
          return raw ? JSON.parse(raw) : [];
        } catch (e) {
          return [];
        }
      })();

      const resolvedIds = new Set(resolvedStores.map((s) => String(s.id)));
      const resolvedNames = new Set(
        resolvedStores.map((s) => String(s.name || s.storeName || "").trim().toLowerCase())
      );

      const preservedInactive = (Array.isArray(currentSaved) ? currentSaved : [])
        .filter((s) => {
          if (!s || !s.id) return false;
          const sProjId = s.projectId ? Number(s.projectId) : activeProjectId;
          if (sProjId !== activeProjectId) return false;
          const isAct = isStoreActive(s.status ?? s.storeStatus);
          if (isAct) return false;
          const idMatches = resolvedIds.has(String(s.id));
          const nameMatches = resolvedNames.has(String(s.name || s.storeName || "").trim().toLowerCase());
          return !idMatches && !nameMatches;
        })
        .map((s) => ({
          ...s,
          status: "inactive",
          storeStatus: "INACTIVE",
          projectId: s.projectId ? Number(s.projectId) : activeProjectId,
        }));

      if (submittedStore && !isStoreActive(submittedStore.status ?? submittedStore.storeStatus)) {
        const subId = String(submittedStore.id);
        const subName = String(submittedStore.name || submittedStore.storeName || "").trim().toLowerCase();
        const alreadyInResolved = resolvedIds.has(subId) || resolvedNames.has(subName);
        const alreadyInPreserved = preservedInactive.some(
          (s) => String(s.id) === subId || String(s.name || s.storeName || "").trim().toLowerCase() === subName
        );
        if (!alreadyInResolved && !alreadyInPreserved) {
          preservedInactive.push({
            ...submittedStore,
            name: submittedStore.name || submittedStore.storeName || "",
            storeName: submittedStore.storeName || submittedStore.name || "",
            status: "inactive",
            storeStatus: "INACTIVE",
            projectId: activeProjectId,
          });
        }
      }

      const allStores = [...resolvedStores, ...preservedInactive];
      setLocalStores(allStores);
      try {
        localStorage.setItem("blufield_stores", JSON.stringify(allStores));
      } catch (e) {}
    } catch (err) {
      console.warn("Could not fetch stores from API, using cached stores:", err);
    } finally {
      setLoading(false);
    }
  }, [activeProjectId, langMode]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  const handleCreateStore = async (storePayload) => {
    try {
      const isInactive = (storePayload.storeStatus || storePayload.status) === "inactive" ||
        String(storePayload.storeStatus || storePayload.status).toUpperCase() === "INACTIVE";
      const formattedStatus = isInactive ? "INACTIVE" : "ACTIVE";
      const response = await createStoreApi({
        projectId: activeProjectId,
        organizationId: activeOrgId,
        storeName: storePayload.storeName || storePayload.name,
        latitude: storePayload.latitude,
        longitude: storePayload.longitude,
        storeStatus: formattedStatus,
      });
      toast.success(t.storeCreatedSuccess || "Store created successfully.");

      const created = response?.body?.value ?? response?.data ?? response;
      const createdId = (typeof created === "object"
        ? created?.id ?? created?.storeId
        : created) ?? storePayload.id;

      saveStoreCache(createdId, {
        id: createdId,
        storeName: storePayload.storeName || storePayload.name,
        latitude: storePayload.latitude,
        longitude: storePayload.longitude,
        location: storePayload.location,
        address: storePayload.address,
        status: isInactive ? "inactive" : "active",
        storeStatus: formattedStatus,
      });

      const normalized = {
        ...storePayload,
        id: createdId,
        status: isInactive ? "inactive" : "active",
        storeStatus: formattedStatus,
      };

      setLocalStores((prev) => {
        const updated = [normalized, ...prev.filter((s) => s.id !== createdId)];
        try {
          localStorage.setItem("blufield_stores", JSON.stringify(updated));
        } catch (e) {}
        return updated;
      });

      setModalOpen(false);
      setEditingStore(null);
      await fetchStores(normalized);
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Failed to create store";
      toast.error(msg);
      throw error;
    }
  };

  const handleUpdateStore = async (storePayload) => {
    try {
      const isInactive = (storePayload.storeStatus || storePayload.status) === "inactive" ||
        String(storePayload.storeStatus || storePayload.status).toUpperCase() === "INACTIVE";
      const formattedStatus = isInactive ? "INACTIVE" : "ACTIVE";
      await updateStoreApi({
        id: Number(storePayload.id),
        projectId: Number(storePayload.projectId || activeProjectId),
        organizationId: activeOrgId,
        storeName: storePayload.storeName || storePayload.name,
        latitude: storePayload.latitude,
        longitude: storePayload.longitude,
        storeStatus: formattedStatus,
      });
      toast.success(t.storeUpdatedSuccess || "Store updated successfully.");

      saveStoreCache(storePayload.id, {
        id: storePayload.id,
        storeName: storePayload.storeName || storePayload.name,
        latitude: storePayload.latitude,
        longitude: storePayload.longitude,
        location: storePayload.location,
        address: storePayload.address,
        status: isInactive ? "inactive" : "active",
        storeStatus: formattedStatus,
      });

      const updatedStore = {
        ...storePayload,
        name: storePayload.storeName || storePayload.name,
        storeName: storePayload.storeName || storePayload.name,
        status: isInactive ? "inactive" : "active",
        storeStatus: formattedStatus,
      };

      setLocalStores((prev) => {
        const updated = prev.map((s) => (s.id === storePayload.id ? { ...s, ...updatedStore } : s));
        try {
          localStorage.setItem("blufield_stores", JSON.stringify(updated));
        } catch (e) {}
        return updated;
      });

      setModalOpen(false);
      setEditingStore(null);
      await fetchStores(updatedStore);
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Failed to update store";
      toast.error(msg);
      throw error;
    }
  };

  const handleViewStore = async (store) => {
    const cache = getStoreCache();
    const cached = cache[String(store.id)] || {};
    let resolved = { ...store, ...cached };

    if (store.id && (resolved.latitude == null || resolved.longitude == null)) {
      try {
        const res = await getStoreByIdApi(store.id);
        const detail = res?.body?.value || res?.data || res;
        if (detail && detail.latitude != null && detail.longitude != null) {
          resolved = {
            ...resolved,
            ...detail,
            latitude: Number(detail.latitude),
            longitude: Number(detail.longitude),
          };
          saveStoreCache(store.id, resolved);
        }
      } catch (e) {}
    }
    setEditingStore(resolved);
    setIsViewOnly(true);
    setModalOpen(true);
  };

  const handleEditStore = async (store) => {
    onEditStore?.(store);
    const cache = getStoreCache();
    const cached = cache[String(store.id)] || {};
    let resolved = { ...store, ...cached };

    if (store.id && (resolved.latitude == null || resolved.longitude == null)) {
      try {
        const res = await getStoreByIdApi(store.id);
        const detail = res?.body?.value || res?.data || res;
        if (detail && detail.latitude != null && detail.longitude != null) {
          resolved = {
            ...resolved,
            ...detail,
            latitude: Number(detail.latitude),
            longitude: Number(detail.longitude),
          };
          saveStoreCache(store.id, resolved);
        }
      } catch (e) {}
    }
    setEditingStore(resolved);
    setIsViewOnly(false);
    setModalOpen(true);
  };

  const handleOpenCreate = () => {
    setEditingStore(null);
    setIsViewOnly(false);
    setModalOpen(true);
  };

  const handleDeleteClick = (store) => {
    setStoreToDelete(store);
  };

  const handleConfirmDelete = async () => {
    if (!storeToDelete) return;
    setIsDeleting(true);
    try {
      if (storeToDelete.id) {
        await deleteStoreApi(storeToDelete.id);
        removeStoreCache(storeToDelete.id);
      }
      setLocalStores((prev) => {
        const updated = prev.filter(
          (s) => String(s.id) !== String(storeToDelete.id) && s.name !== storeToDelete.name && s.storeName !== storeToDelete.storeName
        );
        try {
          localStorage.setItem("blufield_stores", JSON.stringify(updated));
        } catch (e) {}
        return updated;
      });
      toast.success(t.storeDeletedSuccess || "Store deleted successfully.");
      setStoreToDelete(null);
      await fetchStores();
    } catch (error) {
      const errorMsg =
        error.response?.data?.body?.value ||
        error.response?.data?.message ||
        error.message ||
        "Failed to delete store";
      toast.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const scopeOptions = useMemo(
    () => [
      { value: "storeName", label: t.storeName || "Store Name" },
      { value: "location", label: t.storeLocation || "Location" },
      { value: "all", label: translations[langMode]?.all || "All Fields" },
    ],
    [t.storeName, t.storeLocation, langMode]
  );

  const currentPlaceholder = useMemo(() => {
    if (searchScope === "storeName") {
      return `${translations[langMode]?.search || "Search"} ${t.storeName || "Store Name"}...`;
    }
    if (searchScope === "location") {
      return `${translations[langMode]?.search || "Search"} ${t.storeLocation || "Location"}...`;
    }
    return t.searchStores || "Search stores or locations...";
  }, [searchScope, t.storeName, t.storeLocation, t.searchStores, langMode]);

  const hasActiveFilters =
    Boolean(search.trim()) ||
    columnFilters.storeName !== null ||
    columnFilters.location !== null ||
    columnFilters.status !== null;

  const clearAllFilters = () => {
    setSearch("");
    setSearchScope("storeName");
    setColumnFilters({
      storeName: null,
      location: null,
      status: null,
    });
  };

  const filteredStores = useMemo(() => {
    const q = search.trim().toLowerCase();
    return localStores.filter((store) => {
      if (q) {
        const storeNameVal = String(store.name || store.storeName || "").toLowerCase();
        const locationVal = String(store.location || "").toLowerCase();

        if (searchScope === "storeName") {
          if (!storeNameVal.includes(q)) return false;
        } else if (searchScope === "location") {
          if (!locationVal.includes(q)) return false;
        } else {
          if (!storeNameVal.includes(q) && !locationVal.includes(q)) return false;
        }
      }

      const storeNameVal = store.name || store.storeName;
      if (columnFilters.storeName !== null && !columnFilters.storeName.has(storeNameVal)) {
        return false;
      }

      if (columnFilters.location !== null && !columnFilters.location.has(store.location)) {
        return false;
      }

      if (columnFilters.status !== null && !columnFilters.status.has(normalizeStoreStatus(store.status))) {
        return false;
      }

      return true;
    });
  }, [localStores, search, searchScope, columnFilters]);

  const totalStores = filteredStores.length;
  const totalPages = Math.max(1, Math.ceil(totalStores / pageSize));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pageOffset = safePage * pageSize;
  const paginatedStores = filteredStores.slice(pageOffset, pageOffset + pageSize);
  const startIndex = totalStores === 0 ? 0 : pageOffset + 1;
  const endIndex = Math.min(pageOffset + pageSize, totalStores);

  useEffect(() => {
    setCurrentPage(0);
  }, [search, searchScope, columnFilters, pageSize, activeProjectId]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages - 1));
  }, [totalPages]);

  const cardClass = highContrast
    ? "border-white bg-black text-white"
    : dark
      ? "border-slate-700 bg-slate-800 text-white"
      : "border-slate-200 bg-white text-slate-800";

  return (
    <div className={`h-full min-h-0 w-full min-w-0 overflow-y-auto p-2 sm:p-3 ${dark ? "bg-slate-900 text-white" : highContrast ? "bg-black text-white" : "bg-slate-50 text-slate-800"}`}>
      <div className="flex h-full min-h-[600px] w-full min-w-0 flex-col gap-3">
        <div className="flex min-h-[48px] shrink-0 items-center gap-2">
          <BackButton path="/inventory-management" />
          <h1 className="text-lg font-semibold">{t.storeManagement || "Store Management"}</h1>
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
          <section className={`shrink-0 rounded-xl border p-4 shadow-sm sm:p-5 ${cardClass}`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4]"><Store size={20} /></span>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold">{t.createStore}</h2>
                  <p className={`mt-1 text-sm ${dark ? "text-slate-300" : highContrast ? "text-white" : "text-slate-500"}`}>{t.createStoreDescription}</p>
                </div>
              </div>
              <CommonButton colorIntent="primary" onClick={handleOpenCreate} theme={theme} variant="primary" wrapperClassName="w-full shrink-0 sm:w-auto" className="!h-10 !min-h-[40px] w-full !rounded-lg sm:w-auto">
                <Plus size={16} className="shrink-0" />
                {t.createStore}
              </CommonButton>
            </div>
          </section>

          <section className={`flex min-h-[360px] min-w-0 flex-1 flex-col rounded-xl border shadow-sm ${cardClass}`}>
            <div className="shrink-0 border-b border-inherit p-4 sm:p-5">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-base font-semibold">{t.existingStores}</h2>
                  <p className={`mt-1 text-sm ${dark ? "text-slate-300" : highContrast ? "text-white" : "text-slate-500"}`}>{t.existingStoresDescription}</p>
                </div>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="self-start sm:self-auto text-xs font-semibold text-[#236DB4] hover:underline"
                  >
                    {t.clearFilters || "Clear filters"}
                  </button>
                )}
              </div>
              
              {/* Joined SearchFieldPicker + Search Input & Refresh Button (same as Task Management) */}
              <div className="mt-4 flex items-center gap-2.5">
                <div className="flex min-w-0 flex-1 items-center">
                  <SearchFieldPicker
                    options={scopeOptions}
                    activeValue={searchScope}
                    onSelect={(val) => setSearchScope(val)}
                    searchPlaceholder={translations[langMode]?.findAField ?? "Find a field..."}
                    isRtl={isRTL}
                    buttonClassName={
                      highContrast
                        ? "!bg-black !border-white !text-white"
                        : dark
                          ? "!bg-slate-800 !border-slate-600 !text-slate-200 hover:!bg-slate-700"
                          : "!bg-slate-50 !border-slate-200"
                    }
                  />
                  <div className="relative flex min-w-0 flex-1 items-center">
                    <Search
                      size={15}
                      className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${isRTL ? "right-3" : "left-3"}`}
                    />
                    <input
                      type="text"
                      autoComplete="off"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={currentPlaceholder}
                      aria-label={currentPlaceholder}
                      className={`w-full h-9 text-sm truncate border outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4] ${
                        isRTL ? "pr-9 pl-8 border-r-0 rounded-l-lg" : "pl-9 pr-8 border-l-0 rounded-r-lg"
                      } ${
                        highContrast
                          ? "border-white bg-black text-white focus:ring-white"
                          : dark
                            ? "border-slate-600 bg-slate-900 text-white"
                            : "border-slate-200 bg-white text-slate-800 shadow-sm"
                      }`}
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch("")}
                        className={`absolute top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0 ${
                          isRTL ? "left-2" : "right-2"
                        }`}
                        aria-label="Clear search"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={fetchStores}
                  disabled={loading}
                  title={translations[langMode]?.refresh || "Refresh"}
                  aria-label="Refresh stores"
                  className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
                    highContrast
                      ? "border-white bg-black text-white hover:bg-white/10"
                      : dark
                        ? "border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700 hover:border-slate-500 shadow-sm"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#236DB4] hover:border-slate-300 shadow-sm"
                  }`}
                >
                  <RotateCcw size={15} className={`shrink-0 transition-transform ${loading ? "animate-spin text-[#236DB4]" : ""}`} />
                </button>
              </div>
            </div>
            <div className={`relative z-20 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-inherit px-3 py-2.5 text-xs sm:px-5 ${dark ? "text-slate-300" : highContrast ? "text-white" : "text-slate-500"}`}>
              <span>{t.showing || "Showing"} {startIndex}-{endIndex} {t.of || "of"} {totalStores}</span>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span>{t.rowsPerPage || "Rows per page"}</span>
                <PageSizeSelector
                  value={pageSize}
                  onChange={(value) => {
                    setCurrentPage(0);
                    setPageSize(value);
                  }}
                  height="32px"
                  fontSize="12px"
                />
                <span className="pager-nav flex items-center gap-1">
                  <PagerArrowButton
                    direction="prev"
                    isRtl={isRTL}
                    onClick={() => setCurrentPage(Math.max(safePage - 1, 0))}
                    disabled={safePage === 0}
                  />
                  <span className="whitespace-nowrap px-1">{safePage + 1} / {totalPages}</span>
                  <PagerArrowButton
                    direction="next"
                    isRtl={isRTL}
                    onClick={() => setCurrentPage(Math.min(safePage + 1, totalPages - 1))}
                    disabled={safePage >= totalPages - 1}
                  />
                </span>
              </div>
            </div>
            <TableScrollShell theme={theme} fullHeight className="w-full flex-1 min-h-0" innerClassName="!rounded-none !border-0 !shadow-none">
              <table className={`w-full min-w-[620px] text-xs sm:text-sm border-collapse ${filteredStores.length === 0 && !loading ? "h-full" : ""}`}>
                <thead className={`sticky top-0 z-10 border-b ${dark ? "bg-slate-800 text-slate-400 border-slate-700" : highContrast ? "bg-black text-white border-white" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                  <tr className="text-xs font-semibold uppercase tracking-[0.04em]">
                    <th scope="col" className="w-[60px] min-w-[60px] max-w-[60px] px-2 py-3.5 text-center">NO.</th>

                    {/* STORE NAME with column filter */}
                    <th
                      scope="col"
                      className="px-4 py-3.5 text-start cursor-pointer select-none transition-colors hover:bg-slate-100/70 dark:hover:bg-slate-700/50"
                      onClick={() => setOpenFilterPopoverColId((prev) => (prev === "storeName" ? null : "storeName"))}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span>{t.storeName}</span>
                        <button
                          type="button"
                          ref={(el) => { filterButtonRefs.current["storeName"] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilterPopoverColId((prev) => (prev === "storeName" ? null : "storeName"));
                          }}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${
                            columnFilters.storeName !== null
                              ? "text-[#236DB4] bg-blue-50 dark:bg-blue-900/40"
                              : "text-slate-400 hover:text-[#236DB4] hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                          aria-label={`Filter by ${t.storeName}`}
                          title={`Filter by ${t.storeName}`}
                        >
                          <Filter size={12} fill={columnFilters.storeName !== null ? "currentColor" : "none"} />
                        </button>
                      </div>
                      {openFilterPopoverColId === "storeName" && (
                        <ColumnFilterPopover
                          options={filterOptionsByCol.storeName}
                          selected={columnFilters.storeName}
                          onChange={(next) => setColumnFilters((prev) => ({ ...prev, storeName: next }))}
                          onClose={() => setOpenFilterPopoverColId(null)}
                          anchorEl={filterButtonRefs.current["storeName"]}
                          theme={theme}
                          isRtl={isRTL}
                        />
                      )}
                    </th>

                    {/* LOCATION with column filter */}
                    <th
                      scope="col"
                      className="px-4 py-3.5 text-start cursor-pointer select-none transition-colors hover:bg-slate-100/70 dark:hover:bg-slate-700/50"
                      onClick={() => setOpenFilterPopoverColId((prev) => (prev === "location" ? null : "location"))}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span>{t.storeLocation}</span>
                        <button
                          type="button"
                          ref={(el) => { filterButtonRefs.current["location"] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilterPopoverColId((prev) => (prev === "location" ? null : "location"));
                          }}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${
                            columnFilters.location !== null
                              ? "text-[#236DB4] bg-blue-50 dark:bg-blue-900/40"
                              : "text-slate-400 hover:text-[#236DB4] hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                          aria-label={`Filter by ${t.storeLocation}`}
                          title={`Filter by ${t.storeLocation}`}
                        >
                          <Filter size={12} fill={columnFilters.location !== null ? "currentColor" : "none"} />
                        </button>
                      </div>
                      {openFilterPopoverColId === "location" && (
                        <ColumnFilterPopover
                          options={filterOptionsByCol.location}
                          selected={columnFilters.location}
                          onChange={(next) => setColumnFilters((prev) => ({ ...prev, location: next }))}
                          onClose={() => setOpenFilterPopoverColId(null)}
                          anchorEl={filterButtonRefs.current["location"]}
                          theme={theme}
                          isRtl={isRTL}
                        />
                      )}
                    </th>

                    {/* STATUS with column filter */}
                    <th
                      scope="col"
                      className="px-4 py-3.5 text-start cursor-pointer select-none transition-colors hover:bg-slate-100/70 dark:hover:bg-slate-700/50"
                      onClick={() => setOpenFilterPopoverColId((prev) => (prev === "status" ? null : "status"))}
                    >
                      <div className="inline-flex items-center gap-1.5">
                        <span>{t.storeStatus}</span>
                        <button
                          type="button"
                          ref={(el) => { filterButtonRefs.current["status"] = el; }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilterPopoverColId((prev) => (prev === "status" ? null : "status"));
                          }}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${
                            columnFilters.status !== null
                              ? "text-[#236DB4] bg-blue-50 dark:bg-blue-900/40"
                              : "text-slate-400 hover:text-[#236DB4] hover:bg-slate-100 dark:hover:bg-slate-700"
                          }`}
                          aria-label={`Filter by ${t.storeStatus}`}
                          title={`Filter by ${t.storeStatus}`}
                        >
                          <Filter size={12} fill={columnFilters.status !== null ? "currentColor" : "none"} />
                        </button>
                      </div>
                      {openFilterPopoverColId === "status" && (
                        <ColumnFilterPopover
                          options={filterOptionsByCol.status}
                          selected={columnFilters.status}
                          onChange={(next) => setColumnFilters((prev) => ({ ...prev, status: next }))}
                          onClose={() => setOpenFilterPopoverColId(null)}
                          anchorEl={filterButtonRefs.current["status"]}
                          theme={theme}
                          isRtl={isRTL}
                        />
                      )}
                    </th>

                    <th scope="col" className="w-[130px] min-w-[130px] max-w-[130px] px-3 py-3.5 text-center">ACTION</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-inherit">
                  {loading && filteredStores.length === 0 ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <tr key={`skeleton-${i}`} className="border-b border-inherit">
                        <td className="px-2 py-3.5 text-center"><div className="h-4 w-6 mx-auto rounded bg-slate-200 dark:bg-slate-700 animate-pulse" /></td>
                        <td className="px-4 py-3.5"><div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" /></td>
                        <td className="px-4 py-3.5"><div className="h-4 w-48 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" /></td>
                        <td className="px-4 py-3.5"><div className="h-4 w-16 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" /></td>
                        <td className="px-3 py-3.5 text-center"><div className="h-6 w-20 mx-auto rounded bg-slate-200 dark:bg-slate-700 animate-pulse" /></td>
                      </tr>
                    ))
                  ) : filteredStores.length > 0 ? (
                    paginatedStores.map((store, index) => (
                      <tr
                        key={store.id || `${store.name}-${index}`}
                        className={`border-t border-inherit transition-colors duration-150 ${
                          dark ? "hover:bg-slate-800/60" : highContrast ? "hover:bg-slate-900" : "hover:bg-slate-50/80"
                        }`}
                      >
                        <td className="w-[60px] min-w-[60px] max-w-[60px] px-2 py-3.5 text-center font-mono text-xs text-slate-400">
                          {pageOffset + index + 1}
                        </td>
                        <td className="px-4 py-3.5 font-medium">{store.name || store.storeName}</td>
                        <td className="px-4 py-3.5 text-slate-600 dark:text-slate-300">{store.location || "—"}</td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border ${
                              isStoreActive(store.status)
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/40"
                                : "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${
                                isStoreActive(store.status) ? "bg-emerald-500" : "bg-slate-400"
                              }`}
                            />
                            {isStoreActive(store.status) ? t.storeActive : t.storeInactive}
                          </span>
                        </td>
                        <td className="w-[130px] min-w-[130px] max-w-[130px] px-3 py-3.5 text-center whitespace-nowrap">
                          <div className="inline-flex items-center justify-center gap-1.5 sm:gap-2">
                            <ActionIconButton
                              onClick={() => handleViewStore(store)}
                              tone="emerald"
                              ariaLabel={t.viewStore || "View"}
                              tooltipText={t.viewStore || "View"}
                              className="!h-7 !w-7 !rounded-md"
                            >
                              <FiEye size={15} />
                            </ActionIconButton>
                            <ActionIconButton
                              onClick={() => handleEditStore(store)}
                              tone="blue"
                              ariaLabel={t.updateStore || "Update"}
                              tooltipText={t.updateStore || "Update"}
                              className="!h-7 !w-7 !rounded-md"
                            >
                              <Pencil size={14} />
                            </ActionIconButton>
                            <ActionIconButton
                              onClick={() => handleDeleteClick(store)}
                              tone="red"
                              ariaLabel={translations[langMode]?.delete || "Delete"}
                              tooltipText={translations[langMode]?.delete || "Delete"}
                              className="!h-7 !w-7 !rounded-md"
                            >
                              <Trash2 size={14} />
                            </ActionIconButton>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-500">
                        {stores.length === 0 && !loading ? t.noStoresYet : t.noMatchingStores}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableScrollShell>
            <div className={`shrink-0 border-t border-inherit px-4 py-3 text-xs ${dark ? "text-slate-300" : highContrast ? "text-white" : "text-slate-500"}`}>{filteredStores.length} {t.storesCount}</div>
          </section>
        </div>
      </div>
      <CreateStoreModal
        open={modalOpen}
        initialStore={editingStore}
        isViewOnly={isViewOnly}
        onClose={() => {
          setModalOpen(false);
          setEditingStore(null);
          setIsViewOnly(false);
        }}
        onCreateStore={handleCreateStore}
        onUpdateStore={handleUpdateStore}
      />
      {storeToDelete && (
        <DeletePermission
          isLoading={isDeleting}
          onClose={() => setStoreToDelete(null)}
          deleteFunction={handleConfirmDelete}
          onDeleteText={
            translations[langMode]?.aresurewanttodeleterecord ||
            `Are you sure you want to delete ${storeToDelete.storeName || storeToDelete.name || "this store"}?`
          }
        />
      )}
    </div>
  );
};

export default CreateStorePage;
